import { toPrecision } from '@curbnturf/helpers';
import { IAmenity, IBoondock, IBoondockPoint, ISearchableBoondocking } from '../interfaces';
import { Address } from './address';
import { Amenity } from './amenity';
import { AmenityList } from './amenity-list';
import { Boondock } from './boondock';
import { MapPoint } from './map-point';
import { SearchableBoondocking } from './searchable-boondocking';

export class BoondockPoint extends MapPoint implements IBoondockPoint {
  syncId?: string;
  amenities?: IAmenity[];
  price?: number;
  boondock?: ISearchableBoondocking;

  public static toBoondock(boondockPoint: IBoondockPoint): Boondock {
    return new Boondock({
      id: boondockPoint.boondock?.id,
      name: boondockPoint.boondock?.name,
      maxLengthOfStayInDays: boondockPoint.boondock?.maxLengthOfStayInDays,
      photos: boondockPoint.boondock?.photos,
      maxHeight: boondockPoint.boondock?.maxHeight,
      maxWidth: boondockPoint.boondock?.maxWidth,
      maxLength: boondockPoint.boondock?.maxLength,
      description: boondockPoint.boondock?.description,
      rating: boondockPoint.boondock?.rating,
      ratingCount: boondockPoint.boondock?.ratingCount,
      amenities: boondockPoint.boondock?.amenities ? AmenityList.toAmenityArray(boondockPoint.boondock?.amenities) : [],
      coordinates: boondockPoint.boondock?.coordinates,
    });
  }

  public static fromBoondock(boondock: IBoondock): BoondockPoint {
    const searchableBoondocking = SearchableBoondocking.fromBoondock(boondock);

    if (!searchableBoondocking) {
      throw new Error('Unable to convert boondock into a BoondockPoint');
    }

    return BoondockPoint.fromSearchableBoondocking(searchableBoondocking);
  }

  public static fromSearchableBoondocking(boondock: ISearchableBoondocking): BoondockPoint {
    const mapPoint = new this();

    if (boondock.coordinates) {
      mapPoint.coordinates = {
        lat: toPrecision(boondock.coordinates.lat, 2),
        lon: toPrecision(boondock.coordinates.lon, 2),
      };
    }

    if (boondock.id && boondock.name) {
      mapPoint.id = boondock.id;
      mapPoint.name = boondock.name;
    }

    if (boondock.description) {
      mapPoint.description = boondock.description;
    }

    if (boondock.photos) {
      mapPoint.photos = boondock.photos;
    } else {
      mapPoint.photos = [];
    }

    if (boondock.city && boondock.state) {
      mapPoint.location = `${boondock.city}, ${boondock.state}`;
    } else if (boondock.city) {
      mapPoint.location = `${boondock.city}`;
    } else if (boondock.state) {
      mapPoint.location = `${boondock.state}`;
    } else {
      mapPoint.location = '';
    }

    mapPoint.rating = boondock.rating || 0;
    mapPoint.ratingCount = boondock.ratingCount || 0;

    mapPoint.amenities = boondock.amenities ? AmenityList.toAmenityArray(boondock.amenities) : [];

    mapPoint.boondock = new SearchableBoondocking(boondock);

    return mapPoint;
  }

  constructor(data?: IBoondockPoint) {
    super(data);

    if (data) {
      this.setData(data);
    }
  }

  setData(data: IBoondockPoint): void {
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this as any)[key] = (data as any)[key];
      }
    }

    if (this.address) {
      this.address = new Address(this.address);
    }

    if (data.amenities) {
      this.amenities = (data.amenities || []).map((amenity) => {
        if (typeof amenity === 'string') {
          return new Amenity({ type: amenity });
        }

        return new Amenity(amenity);
      });
    }
  }

  getName(): string {
    return this.name || 'Unidentified Boondock';
  }
}
